import {
  ActivationProgressStatus,
  type MerchantApplicationProgressResponse,
} from 'types/responses/activation';
import type { ActivationDashboardStep } from './types';

export { ActivationProgressStatus };

export enum ActivationStepRouteKey {
  BusinessDetails = 'businessDetails',
  DirectorDetails = 'directorDetails',
  SettlementDetails = 'settlementDetails',
}

export const REFERRERS_WITHOUT_SETTLEMENT_DETAILS = new Set(['bupa', 'ndp']);

type DynamicActivationStepConfig = {
  id: number;
  title: string;
  description: string;
  duration: string;
  activeActionLabel: string;
  progressKey: ActivationStepRouteKey;
  routeKey: ActivationStepRouteKey;
};

export const ACTIVATION_DASHBOARD_TOTAL_STEPS = 4;

export const ACCOUNT_VERIFIED_STEP: ActivationDashboardStep = {
  id: 1,
  title: 'Account verified',
  status: 'completed',
};

const DYNAMIC_ACTIVATION_STEPS: DynamicActivationStepConfig[] = [
  {
    id: 2,
    title: 'Add business details',
    description: 'Help us understand a bit more about your business.',
    duration: '1 min',
    activeActionLabel: 'Add details',
    progressKey: ActivationStepRouteKey.BusinessDetails,
    routeKey: ActivationStepRouteKey.BusinessDetails,
  },
  {
    id: 3,
    title: "Verify director's ID",
    description: "You'll need your director's driver's licence or passport.",
    duration: '2 mins',
    activeActionLabel: 'Verify',
    progressKey: ActivationStepRouteKey.DirectorDetails,
    routeKey: ActivationStepRouteKey.DirectorDetails,
  },
  {
    id: 4,
    title: 'Verify settlement details',
    description: 'Confirm bank and disbursement details.',
    duration: '2 mins',
    activeActionLabel: 'Verify',
    progressKey: ActivationStepRouteKey.SettlementDetails,
    routeKey: ActivationStepRouteKey.SettlementDetails,
  },
];

const isProgressConfirmed = (status?: ActivationProgressStatus): boolean =>
  status === ActivationProgressStatus.Confirmed;

export const shouldHideSettlementDetailsForReferrer = (
  applicationReferrer?: string
): boolean =>
  applicationReferrer !== undefined &&
  REFERRERS_WITHOUT_SETTLEMENT_DETAILS.has(applicationReferrer.toLowerCase());

const getVisibleDynamicActivationSteps = (
  isReferredApplication = false
): DynamicActivationStepConfig[] =>
  isReferredApplication
    ? DYNAMIC_ACTIVATION_STEPS.filter(
        (step) => step.routeKey !== ActivationStepRouteKey.SettlementDetails
      )
    : DYNAMIC_ACTIVATION_STEPS;

export const getActivationDashboardTotalSteps = (
  isReferredApplication = false
): number => 1 + getVisibleDynamicActivationSteps(isReferredApplication).length;

export const getCompletedActivationDashboardCount = (
  progress?: MerchantApplicationProgressResponse,
  isReferredApplication = false
): number =>
  1 +
  getVisibleDynamicActivationSteps(isReferredApplication).filter((step) =>
    isProgressConfirmed(progress?.[step.progressKey])
  ).length;

export const buildActivationDashboardSteps = (
  progress: MerchantApplicationProgressResponse | undefined,
  onVerify: (routeKey: ActivationStepRouteKey) => void,
  isReferredApplication = false
): ActivationDashboardStep[] => {
  const visibleDynamicSteps = getVisibleDynamicActivationSteps(
    isReferredApplication
  );
  const firstIncompleteRouteKey = visibleDynamicSteps.find(
    (step) => !isProgressConfirmed(progress?.[step.progressKey])
  )?.routeKey;

  return [
    ACCOUNT_VERIFIED_STEP,
    ...visibleDynamicSteps.map((step): ActivationDashboardStep => {
      const completed = isProgressConfirmed(progress?.[step.progressKey]);

      if (completed) {
        return {
          id: step.id,
          title: step.title,
          description: step.description,
          duration: step.duration,
          status: 'completed',
          secondaryActionLabel: 'Edit',
          onSecondaryAction: (): void => onVerify(step.routeKey),
        };
      }

      const isCurrentStep = step.routeKey === firstIncompleteRouteKey;

      return {
        id: step.id,
        title: step.title,
        description: step.description,
        duration: step.duration,
        status: isCurrentStep ? 'active' : 'upcoming',
        ...(isCurrentStep
          ? {
              actionLabel: step.activeActionLabel,
              onAction: (): void => onVerify(step.routeKey),
            }
          : {}),
      };
    }),
  ];
};

export const ActivationDashboardSteps = buildActivationDashboardSteps(
  undefined,
  () => {}
);
